import React, { useState, useEffect } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Icon } from "semantic-ui-react";
import { Fetchexecutequery, Fetchupdatequery } from '../../../services/process/BRS1/brsService7';
import { CreateData } from "../../../services/process/process_data/process_data";
import LoaderComponent from "../../../libraries/loader/loader";

// ── Constants ─────────────────────────────────────────────────────────────────
const QUESTION_TYPES = [
  { code: "text",        label: "Short Text" },
  { code: "textarea",    label: "Long Text" },
  { code: "number",      label: "Number" },
  { code: "email",       label: "Email" },
  { code: "phone",       label: "Phone Number" },
  { code: "url",         label: "URL / Link" },
  { code: "select",      label: "Dropdown" },
  { code: "yesno",       label: "Yes or No" },
  { code: "radio",       label: "Single Choice" },
  { code: "checkbox",    label: "Multiple Choice" },
  { code: "date",        label: "Date" },
  { code: "date_parts",  label: "Date (Day / Month / Year)" },
  { code: "time",        label: "Time" },
  { code: "datetime",    label: "Date & Time" },
  { code: "rating",      label: "Star Rating" },
  { code: "nps",         label: "NPS (0-10 Scale)" },
  { code: "slider",      label: "Slider / Scale" },
  { code: "file",        label: "File Upload" },
  { code: "signature",   label: "Signature" },
  { code: "matrix",      label: "Matrix / Grid" },
  { code: "ranking",     label: "Ranking" },
  { code: "section",     label: "Section Heading" },
];

// ── Type groups for the two-step category picker ───────────────────────────────
const TYPE_GROUPS = [
  { id: "text",     label: "Text",        types: ["text","textarea","number","email","phone","url"] },
  { id: "choice",   label: "Choice",      types: ["select","yesno","radio","checkbox"] },
  { id: "datetime", label: "Date & Time", types: ["date","date_parts","time","datetime"] },
  { id: "scale",    label: "Scale",       types: ["rating","nps","slider"] },
  { id: "media",    label: "Media",       types: ["file","signature"] },
  { id: "advanced", label: "Advanced",    types: ["matrix","ranking","section"] },
];

const HAS_OPTIONS       = ["select", "radio", "checkbox", "ranking"];
const HAS_MATRIX        = ["matrix"];
const BASE_SURVEY_URL   = process.env.REACT_APP_SURVEY_BASE_URL || "https://pontisacadexusedu.com/survey";

const TYPE_ICONS = {
  text: "Aa", textarea: "¶", number: "#", email: "@", phone: "☎", url: "🔗",
  select: "▾", yesno: "✓✗", radio: "◉", checkbox: "☑",
  date: "📅", date_parts: "📆", time: "⏰", datetime: "🗓",
  rating: "★", nps: "0-10", slider: "⟷", file: "📎", signature: "✍",
  matrix: "⊞", ranking: "↕", section: "§",
};

// ── Config schemas per question type ─────────────────────────────────────────
const TYPE_CONFIG_GROUPS = {
  text: [
    {
      title: "Appearance",
      fields: [
        { key: "placeholder", label: "Placeholder text", type: "text", width: "full", default: "Type your answer here…" },
      ],
    },
    {
      title: "Validation",
      hint: "Leave blank to skip validation",
      fields: [
        { key: "min_length", label: "Min characters", type: "number", width: "half" },
        { key: "max_length", label: "Max characters", type: "number", width: "half" },
      ],
    },
  ],
  textarea: [
    {
      title: "Appearance",
      fields: [
        { key: "placeholder", label: "Placeholder text", type: "text",   width: "full", default: "Type your answer here…" },
        { key: "rows",        label: "Visible rows",     type: "number", width: "half", default: 4 },
      ],
    },
    {
      title: "Validation",
      hint: "Leave blank to skip",
      fields: [
        { key: "max_length", label: "Max characters", type: "number", width: "half" },
      ],
    },
  ],
  number: [
    {
      title: "Appearance",
      fields: [
        { key: "placeholder", label: "Placeholder", type: "text", width: "full", default: "Enter a number" },
      ],
    },
    {
      title: "Range & Step",
      hint: "Leave blank for no limit",
      fields: [
        { key: "min_value", label: "Min value",        type: "number", width: "third" },
        { key: "max_value", label: "Max value",        type: "number", width: "third" },
        { key: "step",      label: "Step / increment", type: "number", width: "third", default: 1 },
      ],
    },
  ],
  email: [
    {
      title: "Appearance",
      fields: [
        { key: "placeholder", label: "Placeholder", type: "text", width: "full", default: "email@example.com" },
      ],
    },
  ],
  phone: [
    {
      title: "Appearance",
      fields: [
        { key: "country_code", label: "Default country code", type: "text", width: "half", default: "+91" },
        { key: "placeholder",  label: "Placeholder",          type: "text", width: "half", default: "Enter phone number" },
      ],
    },
    {
      title: "Validation",
      hint: "Limit the number of digits the phone number can contain",
      fields: [
        { key: "min_digits", label: "Min digits", type: "number", width: "half" },
        { key: "max_digits", label: "Max digits", type: "number", width: "half" },
      ],
    },
  ],
  url: [
    {
      title: "Appearance",
      fields: [
        { key: "placeholder", label: "Placeholder", type: "text", width: "full", default: "https://" },
      ],
    },
  ],
  date: [
    {
      title: "Fields to show",
      hint: "Choose which parts of the date respondents will fill in",
      fields: [
        { key: "show_day",   label: "Day",   type: "toggle", width: "third", default: true },
        { key: "show_month", label: "Month", type: "toggle", width: "third", default: true },
        { key: "show_year",  label: "Year",  type: "toggle", width: "third", default: true },
      ],
    },
    {
      title: "Allowed range",
      hint: "Restrict which dates can be selected",
      fields: [
        { key: "min_date", label: "Earliest date", type: "date", width: "half" },
        { key: "max_date", label: "Latest date",   type: "date", width: "half" },
      ],
    },
    {
      title: "Default",
      fields: [
        { key: "default_today", label: "Pre-fill with today's date", type: "checkbox", width: "full" },
      ],
    },
  ],
  date_parts: [
    {
      title: "Fields to show",
      hint: "Renders separate dropdowns for each chosen part",
      fields: [
        { key: "show_day",   label: "Day",   type: "toggle", width: "third", default: true },
        { key: "show_month", label: "Month", type: "toggle", width: "third", default: true },
        { key: "show_year",  label: "Year",  type: "toggle", width: "third", default: true },
      ],
    },
    {
      title: "Month display format",
      hint: "How the month dropdown shows its options",
      fields: [
        { key: "month_format", label: "Format", type: "radio_inline", width: "full",
          options: [
            { value: "numeric", label: "01 02 … 12" },
            { value: "short",   label: "Jan Feb … Dec" },
            { value: "long",    label: "January February … December" },
          ],
          default: "long",
        },
      ],
    },
    {
      title: "Year range",
      hint: "Sets the min and max year shown in the dropdown",
      fields: [
        { key: "min_year", label: "From year", type: "number", width: "half", default: 1900 },
        { key: "max_year", label: "To year",   type: "number", width: "half", default: new Date().getFullYear() },
      ],
    },
  ],
  time: [
    {
      title: "Format",
      fields: [
        { key: "format_24h",   label: "Use 24-hour clock (HH:MM)",       type: "checkbox", width: "full" },
        { key: "show_seconds", label: "Include seconds field (HH:MM:SS)", type: "checkbox", width: "full" },
      ],
    },
  ],
  datetime: [
    {
      title: "Allowed range",
      hint: "Restrict which date-times can be selected",
      fields: [
        { key: "min_datetime", label: "Earliest", type: "datetime-local", width: "half" },
        { key: "max_datetime", label: "Latest",   type: "datetime-local", width: "half" },
      ],
    },
  ],
  rating: [
    {
      title: "Scale",
      fields: [
        { key: "max_stars", label: "Number of stars", type: "number", width: "half", default: 5 },
        { key: "icon",      label: "Icon style",      type: "radio_inline", width: "half",
          options: [
            { value: "star",  label: "★ Star" },
            { value: "heart", label: "♥ Heart" },
            { value: "thumb", label: "👍 Thumb" },
          ],
          default: "star",
        },
      ],
    },
    {
      title: "End labels",
      hint: "Optional text shown below the lowest and highest rating",
      fields: [
        { key: "show_labels", label: "Show end labels", type: "checkbox", width: "full" },
        { key: "label_low",   label: "Low end label",   type: "text", width: "half", default: "Poor" },
        { key: "label_high",  label: "High end label",  type: "text", width: "half", default: "Excellent" },
      ],
    },
  ],
  nps: [
    {
      title: "End labels",
      hint: "Text shown under 0 (left) and 10 (right)",
      fields: [
        { key: "label_low",  label: "Label for 0",  type: "text", width: "half", default: "Not likely at all" },
        { key: "label_high", label: "Label for 10", type: "text", width: "half", default: "Extremely likely" },
      ],
    },
    {
      title: "Display",
      fields: [
        { key: "show_groups", label: "Show Detractor / Passive / Promoter colour bands", type: "checkbox", width: "full", default: true },
      ],
    },
  ],
  slider: [
    {
      title: "Scale",
      fields: [
        { key: "min_value", label: "Min value", type: "number", width: "third", default: 0 },
        { key: "max_value", label: "Max value", type: "number", width: "third", default: 10 },
        { key: "step",      label: "Step",      type: "number", width: "third", default: 1 },
      ],
    },
    {
      title: "End labels",
      hint: "Optional text shown at the left and right ends of the slider",
      fields: [
        { key: "label_low",  label: "Left label",  type: "text", width: "half" },
        { key: "label_high", label: "Right label", type: "text", width: "half" },
      ],
    },
    {
      title: "Display",
      fields: [
        { key: "show_value", label: "Show selected value next to slider", type: "checkbox", width: "full", default: true },
      ],
    },
  ],
  file: [
    {
      title: "Allowed files",
      fields: [
        { key: "allowed_types", label: "Accepted extensions", type: "text", width: "full", default: ".pdf,.jpg,.png,.docx",
          hint: "Comma-separated, e.g.  .pdf,.jpg,.png" },
        { key: "max_size_mb",   label: "Max file size (MB)", type: "number", width: "half", default: 5 },
      ],
    },
    {
      title: "Behaviour",
      fields: [
        { key: "multiple", label: "Allow uploading multiple files at once", type: "checkbox", width: "full" },
      ],
    },
  ],
  yesno: [
    {
      title: "Button style",
      hint: "How the Yes / No choice appears to respondents",
      fields: [
        { key: "style", label: "", type: "radio_inline", width: "full",
          options: [
            { value: "buttons", label: "✓ / ✗  Big buttons" },
            { value: "toggle",  label: "Toggle switch" },
            { value: "radio",   label: "Radio buttons" },
          ],
          default: "buttons",
        },
      ],
    },
    {
      title: "Button labels",
      hint: "Rename the options if needed (e.g. Agree / Disagree)",
      fields: [
        { key: "label_yes", label: "Positive label", type: "text", width: "half", default: "Yes" },
        { key: "label_no",  label: "Negative label", type: "text", width: "half", default: "No" },
      ],
    },
  ],
  select: [
    {
      title: "Appearance",
      fields: [
        { key: "placeholder", label: "Placeholder (first empty option)", type: "text", width: "full", default: "-- Select an option --" },
      ],
    },
    {
      title: "Behaviour",
      fields: [
        { key: "searchable",  label: "Make dropdown searchable / filterable", type: "checkbox", width: "full" },
        { key: "allow_other", label: "Add an 'Other…' free-text option",      type: "checkbox", width: "full" },
      ],
    },
  ],
  radio: [
    {
      title: "Layout",
      hint: "How the choices are arranged",
      fields: [
        { key: "layout", label: "", type: "radio_inline", width: "full",
          options: [
            { value: "vertical",   label: "↕ Vertical list" },
            { value: "horizontal", label: "↔ Horizontal row" },
            { value: "grid",       label: "⊞ Grid" },
          ],
          default: "vertical",
        },
      ],
    },
    {
      title: "Behaviour",
      fields: [
        { key: "allow_other", label: "Add an 'Other…' free-text option", type: "checkbox", width: "full" },
      ],
    },
  ],
  checkbox: [
    {
      title: "Layout",
      hint: "How the choices are arranged",
      fields: [
        { key: "layout", label: "", type: "radio_inline", width: "full",
          options: [
            { value: "vertical",   label: "↕ Vertical list" },
            { value: "horizontal", label: "↔ Horizontal row" },
            { value: "grid",       label: "⊞ Grid" },
          ],
          default: "vertical",
        },
      ],
    },
    {
      title: "Selection limits",
      hint: "Leave blank for no restriction",
      fields: [
        { key: "min_select", label: "Min selections required", type: "number", width: "half" },
        { key: "max_select", label: "Max selections allowed",  type: "number", width: "half" },
      ],
    },
    {
      title: "Behaviour",
      fields: [
        { key: "allow_other", label: "Add an 'Other…' free-text option", type: "checkbox", width: "full" },
      ],
    },
  ],
  matrix: [
    {
      title: "Behaviour",
      fields: [
        { key: "allow_multiple", label: "Allow selecting multiple answers per row (checkbox mode)", type: "checkbox", width: "full" },
      ],
    },
  ],
  ranking: [
    {
      title: "Appearance",
      fields: [
        { key: "instruction", label: "Instruction shown to respondent", type: "text", width: "full", default: "Drag to rank from most to least preferred" },
      ],
    },
  ],
  section: [
    {
      title: "Section content",
      fields: [
        { key: "description", label: "Description / sub-heading", type: "textarea", width: "full" },
      ],
    },
    {
      title: "Display",
      fields: [
        { key: "divider", label: "Show a divider line below the heading", type: "checkbox", width: "full", default: true },
      ],
    },
  ],
};

// Flatten for "has settings?" check
const TYPE_CONFIG_FIELDS = Object.fromEntries(
  Object.entries(TYPE_CONFIG_GROUPS).map(([k, groups]) => [
    k,
    groups.flatMap(g => g.fields),
  ])
);

const emptyQuestion = (order = 0) => ({
  _key:          Math.random().toString(36).slice(2),
  id:            null,
  label:         "",
  question_type: "text",
  is_required:   0,
  display_order: order,
  options:       [],
  matrix_rows:   [],
  matrix_cols:   [],
  config:        {},
  is_active:     1,
});

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

const pad2 = (n) => String(n).padStart(2, "0");
const normalizeDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const raw = String(value).trim();
  const cleaned = raw.replace(/\.$/, "");
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/
  );
  const cleanedMatch = cleaned.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/
  );

  if (match || cleanedMatch) {
    const [
      ,
      year,
      month,
      day,
      hour = "00",
      minute = "00",
      second = "00",
      fraction = "0",
    ] = match || cleanedMatch;
    const millis = Number((fraction + "000").slice(0, 3));
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      millis
    );
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};
const toDatetimeLocalValue = (value) => {
  const date = normalizeDateTime(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};
const toSqlDatetimeValue = (value) => {
  const date = normalizeDateTime(value);
  if (!date) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}.000000`;
};
const toComparableDateTime = (value) => {
  const date = normalizeDateTime(value);
  return date ? date.getTime() : null;
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function QuestionnaireBuilder() {
  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const [view,       setView]       = useState("list");
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");
  const [searchText, setSearchText] = useState("");
  const [editMode,   setEditMode]   = useState(false);

  const [qnrList, setQnrList] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);

  const [qnrId,       setQnrId]       = useState(null);
  const [title,       setTitle]       = useState("");
  const [slug,        setSlug]        = useState("");
  const [description, setDescription] = useState("");
  const [eventMasterId, setEventMasterId] = useState("");
  const [formStartDate,  setFormStartDate]  = useState("");
  const [formEndDate,    setFormEndDate]    = useState("");
  const [questions,   setQuestions]   = useState([emptyQuestion(0)]);

  // Config panel state
  const [openConfigIdx, setOpenConfigIdx] = useState(null);
  const [configTabIdx,  setConfigTabIdx]  = useState({});

  // ── NEW: per-question open category for the two-step type picker ─────────────
  const [openCatIdx, setOpenCatIdx] = useState({}); // { [qi]: catId | null }
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState(null);

  const [secHeader,    setSecHeader]    = useState(true);
  const [secQuestions, setSecQuestions] = useState(true);

  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrTitle,   setQrTitle]   = useState("");
  const [qrSlug,    setQrSlug]    = useState("");

  const selectedEvent = eventOptions.find((event) => String(event.id) === String(eventMasterId)) || null;

  // ── Column defs ──────────────────────────────────────────────────────────────
  const columnDefs = [
    { headerName: "Title",   field: "title",        sortable: true, filter: true, flex: 2 },
    { headerName: "Slug",    field: "slug",          sortable: true, filter: true, flex: 2 },
    {
      headerName: "Event",
      field: "event_name",
      sortable: true,
      filter: true,
      flex: 2,
      valueGetter: (p) => {
        const event = eventOptions.find((item) => String(item.id) === String(p.data?.event_master_id));
        return event?.label || p.data?.event_master_id || "";
      },
    },
    { headerName: "Form Start", field: "form_start_date", sortable: true, filter: true, flex: 1.4 },
    { headerName: "Form End", field: "form_end_date", sortable: true, filter: true, flex: 1.4 },
    {
      headerName: "Status", field: "is_active", sortable: true, flex: 1,
      cellRendererFramework: (p) => (
        <span style={{ color: p.value ? "#1a7a4a" : "#999", fontWeight: 600 }}>
          {p.value ? "Active" : "Inactive"}
        </span>
      ),
    },
    { headerName: "Created", field: "created_date", sortable: true, flex: 2 },
    {
      headerName: "Edit", field: "edit", width: 80,
      cellRendererFramework: (p) => (
        <Icon name="edit"
          style={{ cursor: "pointer", color: "#0a2352" }}
          onClick={() => handleEditClick(p.data)}
        />
      ),
    },
    {
      headerName: "QR", field: "qr", width: 70,
      cellRendererFramework: (p) => (
        <Icon name="qrcode"
          style={{ cursor: "pointer", color: "#0a2352" }}
          onClick={() => openQrView(p.data.title, p.data.slug, p.data.qr_code_url)}
        />
      ),
    },
    {
      headerName: "Delete", field: "delete", width: 80,
      cellRendererFramework: (p) => (
        <Icon name="trash"
          style={{ cursor: "pointer", color: "red" }}
          onClick={() => handleDeleteQnr(p.data)}
        />
      ),
    },
  ];

  useEffect(() => { fetchQnrList(); fetchEventOptions(); }, []);

  // ── READ ────────────────────────────────────────────────────────────────────
  const fetchQnrList = async () => {
    setLoading(true);
    const payload = {
      query: `SELECT id, title, slug, description, event_master_id, form_start_date, form_end_date, is_active, qr_code_url,
                     date(created_date) AS created_date
              FROM dynamic_form.questionnaires
              WHERE is_active = 1
              ORDER BY id DESC;`,
    };
    try {
      const creds = Fetchexecutequery();
      const res   = await axios.post(creds[1], payload, creds[0]);
      setQnrList(res.data || []);
    } catch (err) {
      console.error("fetchQnrList error:", err);
      setError("Failed to load questionnaires");
    }
    setLoading(false);
  };

  const fetchEventOptions = async () => {
    const payload = {
    query: `SELECT *
              FROM education.events
              WHERE is_active = 1;`,
    };
    try {
      const creds = Fetchexecutequery();
      const res   = await axios.post(creds[1], payload, creds[0]);
      const rows = (res.data || []).map((row) => {
        const id = row.primary_id ?? row.id ?? row.event_master_id ?? null;
        const label = row.event_name ?? row.event_master_name ?? row.name ?? row.title ?? row.event_title ?? (id ? `Event ${id}` : "");
        return {
          id,
          label,
          start_datetime: row.start_datetime ?? row.start_date ?? "",
          end_datetime: row.end_datetime ?? row.end_date ?? "",
        };
      }).filter((row) => row.id !== null && row.id !== undefined);
      setEventOptions(rows);
    } catch (err) {
      console.error("fetchEventOptions error:", err);
      setError("Failed to load event names");
    }
  };

  const fetchQuestions = async (questionnaire_id) => {
    const payload = {
      query: `SELECT q.id, q.label, q.question_type, q.is_required,
                     q.display_order, q.is_active, q.config,
                     GROUP_CONCAT(
                       CONCAT(o.option_label,'||',o.option_value,'||',o.display_order)
                       ORDER BY o.display_order SEPARATOR ';;'
                     ) AS options_raw
              FROM dynamic_form.questions q
              LEFT JOIN dynamic_form.question_options o
                ON o.question_id = q.id AND o.is_active = 1
              WHERE q.questionnaire_id = ${questionnaire_id}
                AND q.is_active = 1
              GROUP BY q.id
              ORDER BY q.display_order;`,
    };
    try {
      const creds = Fetchexecutequery();
      const res   = await axios.post(creds[1], payload, creds[0]);
      return (res.data || []).map((row) => {
        const parsed = (() => {
          const c = row.config;
          if (!c) return {};
          if (typeof c === "object") return c;
          try { return JSON.parse(c); } catch { return {}; }
        })();
        return {
          _key:          row.id.toString(),
          id:            row.id,
          label:         row.label,
          question_type: row.question_type,
          is_required:   row.is_required,
          display_order: row.display_order,
          is_active:     row.is_active,
          config:        parsed,
          matrix_rows:   parsed.matrix_rows || [],
          matrix_cols:   parsed.matrix_cols || [],
          options: row.options_raw
            ? row.options_raw.split(";;").map((part) => {
                const [option_label, option_value, display_order] = part.split("||");
                return { option_label, option_value, display_order: Number(display_order) };
              })
            : [],
        };
      });
    } catch (err) {
      console.error("fetchQuestions error:", err);
      return [];
    }
  };

  // ── CREATE ──────────────────────────────────────────────────────────────────
  const createQnr = async (now, qrUrl) => {
    const payload = {
      database: "dynamic_form",
      table: "questionnaires",
      formData: {
        title, slug, description,
        event_master_id: eventMasterId || null,
        form_start_date: toSqlDatetimeValue(formStartDate),
        form_end_date: toSqlDatetimeValue(formEndDate),
        qr_code_url: qrUrl,
        is_active: 1,
        created_date: now,
        modified_date: now,
        tenant_id:   userCredentials.tenants_id,
        entity_id:   userCredentials.selectedentity,
        created_by:  userCredentials.user_id,
      },
    };
    const creds = CreateData();
    await axios.post(creds[1], payload, creds[0]);
  };

  const fetchQuestionnaireId = async () => {
    const payload = { query: `SELECT id FROM dynamic_form.questionnaires WHERE slug = '${slug}' ORDER BY id DESC LIMIT 1;` };
    try {
      const creds = Fetchexecutequery();
      const res   = await axios.post(creds[1], payload, creds[0]);
      return res.data?.[0]?.id || null;
    } catch (err) { return null; }
  };

  const fetchQuestionId = async (questionnaire_id, label) => {
    const payload = {
      query: `SELECT id FROM dynamic_form.questions WHERE questionnaire_id = ${questionnaire_id} AND label = '${label.replace(/'/g, "''")}' ORDER BY id DESC LIMIT 1;`
    };
    try {
      const creds = Fetchexecutequery();
      const res   = await axios.post(creds[1], payload, creds[0]);
      return res.data?.[0]?.id || null;
    } catch (err) { return null; }
  };

  const createQuestion = async (q, order, questionnaire_id, now) => {
    const payload = {
      database: "dynamic_form",
      table:    "questions",
      formData: {
        questionnaire_id,
        label:         q.label,
        question_type: q.question_type,
        is_required:   q.is_required,
        display_order: order,
        config:        JSON.stringify({
          ...(q.config || {}),
          ...(q.matrix_rows?.length ? { matrix_rows: q.matrix_rows } : {}),
          ...(q.matrix_cols?.length ? { matrix_cols: q.matrix_cols } : {}),
        }),
        is_active:     1,
        created_date:  now,
        modified_date: now,
        tenant_id:     userCredentials.tenants_id,
        entity_id:     userCredentials.selectedentity,
        created_by:    userCredentials.user_id,
      },
    };
    const creds = CreateData();
    await axios.post(creds[1], payload, creds[0]);
  };

  const createOption = async (opt, order, question_id, now) => {
    const payload = {
      database: "dynamic_form",
      table:    "question_options",
      formData: {
        question_id,
        option_label:  opt.option_label,
        option_value:  opt.option_value,
        display_order: order,
        is_active:     1,
        created_date:  now,
        tenant_id:     userCredentials.tenants_id,
        entity_id:     userCredentials.selectedentity,
      },
    };
    const creds = CreateData();
    await axios.post(creds[1], payload, creds[0]);
  };

  const softDeleteQuestionOptions = async (question_id) => {
    const payload = {
      schema_name: "dynamic_form",
      table: "question_options",
      set_column: [{ is_active: 0 }],
      where: [{ question_id }],
    };
    const creds = Fetchupdatequery();
    await axios.post(creds[1], payload, creds[0]);
  };

  // ── UPDATE ──────────────────────────────────────────────────────────────────
  const updateQnr = async (id, now) => {
    const payload = {
      schema_name: "dynamic_form",
      table:       "questionnaires",
      set_column:  [{
        title,
        description,
        event_master_id: eventMasterId || null,
        form_start_date: toSqlDatetimeValue(formStartDate),
        form_end_date: toSqlDatetimeValue(formEndDate),
        modified_date: now,
        modified_by: userCredentials.user_id,
      }],
      where: [{ id }],
    };
    const creds = Fetchupdatequery();
    await axios.post(creds[1], payload, creds[0]);
  };

  const updateQuestion = async (q, order, now) => {
    const payload = {
      schema_name: "dynamic_form",
      table:       "questions",
      set_column:  [{
        label: q.label, question_type: q.question_type,
        is_required: q.is_required, display_order: order,
        config: JSON.stringify({
          ...(q.config || {}),
          ...(q.matrix_rows?.length ? { matrix_rows: q.matrix_rows } : {}),
          ...(q.matrix_cols?.length ? { matrix_cols: q.matrix_cols } : {}),
        }),
        modified_date: now, modified_by: userCredentials.user_id,
      }],
      where: [{ id: q.id }],
    };
    const creds = Fetchupdatequery();
    await axios.post(creds[1], payload, creds[0]);
  };

  const softDeleteQuestion = async (id) => {
    const payload = { schema_name: "dynamic_form", table: "questions", set_column: [{ is_active: 0 }], where: [{ id }] };
    const creds = Fetchupdatequery();
    await axios.post(creds[1], payload, creds[0]);
  };

  const handleDeleteQnr = async (row) => {
    if (!window.confirm(`Delete questionnaire "${row.title}"?`)) return;
    const payload = {
      schema_name: "dynamic_form",
      table:       "questionnaires",
      set_column:  [{ is_active: 0, modified_date: getFormattedDate() }],
      where:       [{ id: row.id }],
    };
    const creds = Fetchupdatequery();
    await axios.post(creds[1], payload, creds[0]);
    fetchQnrList();
  };

  // ── SAVE ORCHESTRATION ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setError("");
    if (!title.trim()) return setError("Title is required");
    if (!slug.trim())  return setError("Slug is required");
    if (!eventMasterId) return setError("Event name is required");
    if (!formStartDate) return setError("Form start date is required");
    if (!formEndDate) return setError("Form end date is required");

    const formStartTs = toComparableDateTime(formStartDate);
    const formEndTs = toComparableDateTime(formEndDate);
    const eventStartTs = toComparableDateTime(selectedEvent?.start_datetime);
    const eventEndTs = toComparableDateTime(selectedEvent?.end_datetime);

    if (formStartTs === null || formEndTs === null) return setError("Please enter valid form dates");
    if (formStartTs > formEndTs) return setError("Form start date must be before form end date");
    if (eventStartTs !== null && formStartTs < eventStartTs) {
        return setError("Form start date must be on or after the selected event start date");
    }
    if (eventEndTs !== null && formEndTs > eventEndTs) {
        return setError("Form end date must be on or before the selected event end date");
    }
    const nonSections = questions.filter(q => q.question_type !== "section");
    if (nonSections.some((q) => !q.label.trim()))
      return setError("All questions must have a label");

    setSaving(true);
    const now = getFormattedDate();
    try {
      let resolvedQnrId = qnrId;

      if (editMode) {
        await updateQnr(resolvedQnrId, now);
      } else {
        const qrUrl = await QRCode.toDataURL(`${BASE_SURVEY_URL}/${slug}`, { width: 300 });
        await createQnr(now, qrUrl);
        resolvedQnrId = await fetchQuestionnaireId();
        if (!resolvedQnrId) throw new Error("Unable to fetch questionnaire ID");
        setQnrId(resolvedQnrId);
        setQrDataUrl(qrUrl);
        setQrTitle(title);
        setQrSlug(slug);
      }

      const savedQuestions = [];
      for (let i = 0; i < questions.length; i++) {
        const q = { ...questions[i] };
        let questionId = q.id;

        if (q.id) {
          await updateQuestion(q, i, now);
        } else {
          await createQuestion(q, i, resolvedQnrId, now);
          questionId = await fetchQuestionId(resolvedQnrId, q.label);
          if (!questionId) throw new Error(`Unable to fetch question ID for ${q.label}`);
        }

        const typesWithOptions = [...HAS_OPTIONS, ...HAS_MATRIX];
        if (q.id) {
          await softDeleteQuestionOptions(questionId);
        }

        if (typesWithOptions.includes(q.question_type) && q.options.length) {
          for (let j = 0; j < q.options.length; j++) {
            const opt = q.options[j];
            if (!opt.option_label.trim()) continue;
            await createOption(opt, j, questionId, now);
          }
        }

        savedQuestions.push({ ...q, id: questionId, _key: String(questionId || q._key) });
      }

      setQuestions(savedQuestions);
      setEditMode(true);
      await fetchQnrList();
      if (!editMode) setView("qr");

    } catch (err) {
      console.error("handleSave error:", err);
      setError("Save failed: " + (err.response?.data?.message || err.message));
    }
    setSaving(false);
  };

  // ── EDIT / QR helpers ────────────────────────────────────────────────────────
  const handleEditClick = async (row) => {
    resetForm();
    setEditMode(true);
    setView("builder");
    setQnrId(row.id);
    setTitle(row.title);
    setSlug(row.slug);
    setDescription(row.description || "");
    setEventMasterId(row.event_master_id ?? "");
    setFormStartDate(row.form_start_date ? toDatetimeLocalValue(row.form_start_date) : "");
    setFormEndDate(row.form_end_date ? toDatetimeLocalValue(row.form_end_date) : "");
    const qs = await fetchQuestions(row.id);
    setQuestions(qs.length ? qs : [emptyQuestion(0)]);
  };

  const openQrView = async (t, s, storedUrl) => {
    if (storedUrl) {
      setQrDataUrl(storedUrl);
    } else {
      const url = await QRCode.toDataURL(`${BASE_SURVEY_URL}/${s}`, { width: 300 });
      setQrDataUrl(url);
    }
    setQrTitle(t); setQrSlug(s); setView("qr");
  };

  const handleDownloadQR = () => {
    const link = document.createElement("a");
    link.href  = qrDataUrl;
    link.download = `qr-${qrSlug}.png`;
    link.click();
  };

  // ── LOCAL QUESTION STATE HANDLERS ───────────────────────────────────────────
  const handleTitleChange = (val) => {
    setTitle(val);
    if (!editMode)
      setSlug(val.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""));
  };

  const addQuestion = () =>
    setQuestions((prev) => [...prev, emptyQuestion(prev.length)]);

  const removeQuestion = async (idx) => {
    const q = questions[idx];
    if (q.id) await softDeleteQuestion(q.id);
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
    // clean up per-question state
    setOpenConfigIdx((prev) => remapScalarIndexForRemoval(prev, idx));
    setOpenCatIdx((prev) => remapIndexedStateForRemoval(prev, idx));
    setConfigTabIdx((prev) => remapIndexedStateForRemoval(prev, idx));
  };

  const updateQuestionLocal = (idx, patch) =>
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));

  const remapIndexedStateForMove = (state, fromIndex, toIndex) => {
    const oldOrder = questions.map((_, idx) => idx);
    const [moved] = oldOrder.splice(fromIndex, 1);
    oldOrder.splice(toIndex, 0, moved);

    const next = {};
    oldOrder.forEach((oldIdx, newIdx) => {
      if (state && Object.prototype.hasOwnProperty.call(state, oldIdx)) {
        next[newIdx] = state[oldIdx];
      }
    });
    return next;
  };

  const remapScalarIndexForMove = (index, fromIndex, toIndex) => {
    if (index === null || index === undefined) return index;
    if (index === fromIndex) return toIndex;
    if (fromIndex < toIndex && index > fromIndex && index <= toIndex) return index - 1;
    if (toIndex < fromIndex && index >= toIndex && index < fromIndex) return index + 1;
    return index;
  };

  const remapIndexedStateForRemoval = (state, removedIndex) => {
    const next = {};
    Object.keys(state || {}).forEach((key) => {
      const idx = Number(key);
      if (idx === removedIndex) return;
      const newIdx = idx > removedIndex ? idx - 1 : idx;
      next[newIdx] = state[key];
    });
    return next;
  };

  const remapScalarIndexForRemoval = (index, removedIndex) => {
    if (index === null || index === undefined) return index;
    if (index === removedIndex) return null;
    return index > removedIndex ? index - 1 : index;
  };

  const moveQuestion = (fromIndex, toIndex) => {
    if (
      fromIndex === null ||
      toIndex === null ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= questions.length ||
      toIndex >= questions.length
    ) {
      return;
    }

    setQuestions((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

    setOpenConfigIdx((prev) => remapScalarIndexForMove(prev, fromIndex, toIndex));
    setConfigTabIdx((prev) => remapIndexedStateForMove(prev, fromIndex, toIndex));
    setOpenCatIdx((prev) => remapIndexedStateForMove(prev, fromIndex, toIndex));
  };

  const updateConfig = (idx, key, value) =>
    setQuestions((prev) =>
      prev.map((q, i) => i !== idx ? q : { ...q, config: { ...q.config, [key]: value } })
    );

  const addOption = (idx) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i !== idx ? q
          : { ...q, options: [...q.options, { option_label: "", option_value: "", display_order: q.options.length }] }
      )
    );

  const removeOption = (qIdx, oIdx) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i !== qIdx ? q : { ...q, options: q.options.filter((_, j) => j !== oIdx) }
      )
    );

  const handleQuestionDragStart = (idx) => {
    setDraggedQuestionIndex(idx);
  };

  const handleQuestionDrop = (idx) => {
    if (draggedQuestionIndex === null) return;
    moveQuestion(draggedQuestionIndex, idx);
    setDraggedQuestionIndex(null);
  };

  const handleQuestionDragEnd = () => {
    setDraggedQuestionIndex(null);
  };

  const moveQuestionUp = (idx) => moveQuestion(idx, idx - 1);
  const moveQuestionDown = (idx) => moveQuestion(idx, idx + 1);

  const handleOptionLabel = (qIdx, oIdx, label) => {
    const value = label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIdx) return q;
        const options = q.options.map((o, j) =>
          j === oIdx ? { ...o, option_label: label, option_value: value } : o
        );
        return { ...q, options };
      })
    );
  };

  // Matrix row/col helpers
  const addMatrixRow = (idx) =>
    setQuestions((prev) =>
      prev.map((q, i) => i !== idx ? q : { ...q, matrix_rows: [...(q.matrix_rows || []), ""] })
    );
  const addMatrixCol = (idx) =>
    setQuestions((prev) =>
      prev.map((q, i) => i !== idx ? q : { ...q, matrix_cols: [...(q.matrix_cols || []), ""] })
    );
  const updateMatrixRow = (qIdx, rIdx, val) =>
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIdx) return q;
        const rows = [...(q.matrix_rows || [])];
        rows[rIdx] = val;
        return { ...q, matrix_rows: rows };
      })
    );
  const updateMatrixCol = (qIdx, cIdx, val) =>
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIdx) return q;
        const cols = [...(q.matrix_cols || [])];
        cols[cIdx] = val;
        return { ...q, matrix_cols: cols };
      })
    );

  const resetForm = () => {
    setQnrId(null); setTitle(""); setSlug(""); setDescription("");
    setEventMasterId(""); setFormStartDate(""); setFormEndDate("");
    setQuestions([emptyQuestion(0)]); setError(""); setEditMode(false);
    setOpenConfigIdx(null); setConfigTabIdx({}); setOpenCatIdx({});
  };

  // ── CONFIG PANEL RENDERER ────────────────────────────────────────────────────
  const renderConfigField = (field, q, qi) => {
    const val = q.config?.[field.key] ?? (field.default !== undefined ? field.default : "");
    const widthMap = { full: "100%", half: "calc(50% - 6px)", third: "calc(33.33% - 8px)" };
    const w = widthMap[field.width || "half"];
    const inputStyle = { ...styles.configInput, width: "100%" };

    let control;

    if (field.type === "toggle") {
      const isOn = val === true || val === "true" || val === 1;
      control = (
        <button
          type="button"
          onClick={() => updateConfig(qi, field.key, !isOn)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "7px 14px", borderRadius: 20, width: "100%",
            border: `1.5px solid ${isOn ? "#4969ab" : "#ccc"}`,
            background: isOn ? "#eef3fc" : "#fafafa",
            color: isOn ? "#0a2352" : "#999",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            transition: "all 0.18s",
          }}
        >
          <span style={{
            width: 10, height: 10, borderRadius: "50%",
            background: isOn ? "#4969ab" : "#ccc",
            display: "inline-block", flexShrink: 0,
          }} />
          {field.label}
        </button>
      );
    } else if (field.type === "checkbox") {
      const isOn = val === true || val === "true" || val === 1;
      control = (
        <label style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px", borderRadius: 8,
          border: `1.5px solid ${isOn ? "#4969ab" : "#dde0e8"}`,
          background: isOn ? "#eef3fc" : "#fafafa",
          cursor: "pointer", fontSize: 13,
          color: isOn ? "#0a2352" : "#555",
          transition: "all 0.18s",
        }}>
          <input
            type="checkbox"
            checked={isOn}
            onChange={(e) => updateConfig(qi, field.key, e.target.checked)}
            style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#4969ab" }}
          />
          {field.label}
        </label>
      );
    } else if (field.type === "radio_inline") {
      control = (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {field.options.map((opt) => {
            const selected = (val || field.default) === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateConfig(qi, field.key, opt.value)}
                style={{
                  padding: "7px 14px", borderRadius: 8,
                  border: `1.5px solid ${selected ? "#4969ab" : "#dde0e8"}`,
                  background: selected ? "#eef3fc" : "#fafafa",
                  color: selected ? "#0a2352" : "#666",
                  fontSize: 12, fontWeight: selected ? 700 : 400,
                  cursor: "pointer", transition: "all 0.18s",
                  whiteSpace: "nowrap",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );
    } else if (field.type === "textarea") {
      control = (
        <textarea
          style={{ ...inputStyle, height: 64, resize: "vertical" }}
          value={val}
          onChange={(e) => updateConfig(qi, field.key, e.target.value)}
          placeholder={field.default ? String(field.default) : ""}
        />
      );
    } else {
      control = (
        <input
          type={field.type}
          style={inputStyle}
          value={val}
          onChange={(e) => updateConfig(qi, field.key, e.target.value)}
          placeholder={field.default ? String(field.default) : ""}
        />
      );
    }

    return (
      <div key={field.key} style={{ width: w, minWidth: 120 }}>
        {field.label && field.type !== "checkbox" && field.type !== "toggle" && field.type !== "radio_inline" && (
          <label style={styles.configLabel}>{field.label}</label>
        )}
        {control}
        {field.hint && (
          <span style={{ fontSize: 10, color: "#aaa", display: "block", marginTop: 3 }}>{field.hint}</span>
        )}
      </div>
    );
  };

  const renderConfigPanel = (q, qi) => {
    const groups = TYPE_CONFIG_GROUPS[q.question_type];
    if (!groups || groups.length === 0) return null;

    const activeTab = configTabIdx[qi] ?? 0;
    const group     = groups[activeTab] || groups[0];

    return (
      <div style={styles.configPanel}>
        {/* Panel header */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{
            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
            background: "#eef3fc", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="setting" style={{ fontSize: 11, color: "#4969ab", margin: 0 }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0a2352" }}>Settings</span>
          <span style={{
            marginLeft: "auto", fontSize: 10, color: "#4969ab",
            background: "#eef3fc", padding: "2px 8px", borderRadius: 10, fontWeight: 600,
          }}>
            {QUESTION_TYPES.find(t => t.code === q.question_type)?.label}
          </span>
        </div>

        {/* Tab bar */}
        {groups.length > 1 && (
          <div style={{
            display: "flex", gap: 0, marginBottom: 14,
            borderBottom: "2px solid #e4e8f2", overflowX: "auto",
          }}>
            {groups.map((g, gi) => {
              const isActive = gi === activeTab;
              return (
                <button
                  key={gi}
                  type="button"
                  onClick={() => setConfigTabIdx(prev => ({ ...prev, [qi]: gi }))}
                  style={{
                    padding: "7px 16px", fontSize: 12, fontWeight: isActive ? 700 : 400,
                    color: isActive ? "#0a2352" : "#888",
                    background: "none", border: "none", cursor: "pointer",
                    borderBottom: `2px solid ${isActive ? "#4969ab" : "transparent"}`,
                    marginBottom: "-2px", whiteSpace: "nowrap",
                    transition: "all 0.15s",
                  }}
                >
                  {g.title}
                </button>
              );
            })}
          </div>
        )}

        {/* Single-group label */}
        {groups.length === 1 && (
          <div style={{ marginBottom: 10 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.07em", color: "#4969ab",
            }}>
              {group.title}
            </span>
            {group.hint && (
              <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>— {group.hint}</span>
            )}
          </div>
        )}

        {/* Active group hint (tabbed view) */}
        {groups.length > 1 && group.hint && (
          <p style={{ fontSize: 11, color: "#999", margin: "0 0 10px", fontStyle: "italic" }}>
            {group.hint}
          </p>
        )}

        {/* Fields */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start" }}>
          {group.fields.map((field) => renderConfigField(field, q, qi))}
        </div>
      </div>
    );
  };

  // ── Type badge color ──────────────────────────────────────────────────────────
  const typeColor = (code) => {
    const map = {
      text: "#e3f0fb", textarea: "#e3f0fb", number: "#fff3e0", email: "#fff3e0",
      phone: "#fff3e0", url: "#fff3e0", select: "#e8f5e9", yesno: "#e8f5e9",
      radio: "#e8f5e9", checkbox: "#e8f5e9", date: "#fce4ec", date_parts: "#fce4ec",
      time: "#fce4ec", datetime: "#fce4ec", rating: "#f3e5f5", nps: "#f3e5f5",
      slider: "#f3e5f5", file: "#fff8e1", signature: "#fff8e1", matrix: "#e0f2f1",
      ranking: "#e0f2f1", section: "#f5f5f5",
    };
    return map[code] || "#f5f5f5";
  };

  if (loading) return <LoaderComponent />;

  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <h2 className="pt-1 pb-1 text-center" style={{ color: "#0a2352", fontSize: 18 }}>
        SURVEY QUESTIONNAIRE BUILDER
      </h2>

      {/* LIST VIEW */}
      {view === "list" && (
        <div>
          <div className="container-fluid d-flex justify-content-end align-items-center mb-2">
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={styles.searchInput}
            />
            <div
              onClick={() => { resetForm(); setView("builder"); }}
              style={styles.iconBtn("#007bff")}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#0056b3")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#007bff")}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="ag-theme-alpine"
            style={{ height: 540, margin: "0 15px", borderRadius: 10, overflow: "hidden" }}>
            <AgGridReact
              columnDefs={columnDefs}
              rowData={qnrList}
              quickFilterText={searchText}
              animateRows pagination paginationPageSize={12}
            />
          </div>
        </div>
      )}

      {/* BUILDER VIEW */}
      {view === "builder" && (
        <div style={{ padding: "0 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0" }}>
            <div style={{ display: "flex", gap: 20 }}>
              <div className="prf-container">
                <span className="prf-label">Mode:</span>
                <input type="text" value={editMode ? "Edit" : "New"} readOnly
                  className="prf-input" style={{ marginLeft: 10, width: 80 }} />
              </div>
              {editMode && (
                <div className="prf-container">
                  <span className="prf-label">Slug:</span>
                  <input type="text" value={slug} readOnly
                    className="prf-input" style={{ marginLeft: 10 }} />
                </div>
              )}
            </div>
            <div
              onClick={() => { resetForm(); setView("list"); fetchQnrList(); }}
              style={styles.iconBtn("#0a2352")}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#218838")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0a2352")}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M3 5H21M3 12H21M3 19H21" stroke="white" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          {error && <div style={{ color: "#c0392b", marginBottom: 10, fontSize: 13 }}>{error}</div>}

          {/* Section 1: Header */}
          <div style={{ margin: "10px" }}>
            <h3 onClick={() => setSecHeader((v) => !v)}
              style={{ cursor: "pointer", color: "#4969ab", paddingLeft: 20,
                display: "flex", alignItems: "center" }}>
              Questionnaire Details
              <span style={{ fontSize: 24, fontWeight: "bold", marginLeft: 10 }}>
                {secHeader ? "−" : "+"}
              </span>
            </h3>
            {secHeader && (
              <div style={{ padding: "0 10px" }}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={styles.label}>Title *</label>
                    <input style={styles.input} value={title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="e.g. Customer Satisfaction Survey" />
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={styles.label}>Slug *</label>
                    <input style={styles.input} value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      readOnly={editMode}
                      placeholder="auto-generated from title" />
                    <span style={styles.hint}>
                      Survey URL: /survey/<strong>{slug || "…"}</strong>
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "nowrap", overflowX: "auto", marginBottom: 12, paddingBottom: 4 }}>
                  <div style={{ flex: "0 0 240px", minWidth: 240 }}>
                    <label style={styles.label}>Event Name *</label>
                    <select
                      style={styles.input}
                      value={eventMasterId}
                      onChange={(e) => setEventMasterId(e.target.value)}
                    >
                      <option value="">Select an event</option>
                      {eventOptions.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.label || `Event ${event.id}`}
                        </option>
                      ))}
                    </select>
                    <span style={styles.hint}>Choose the event this questionnaire belongs to</span>
                  </div>
                  <div style={{ flex: "0 0 195px", minWidth: 195 }}>
                    <label style={styles.label}>Event Start Datetime</label>
                    <input
                      type="datetime-local"
                      style={styles.input}
                      value={toDatetimeLocalValue(selectedEvent?.start_datetime)}
                      readOnly
                      placeholder="Selected event start datetime"
                    />
                  </div>
                  <div style={{ flex: "0 0 195px", minWidth: 195 }}>
                    <label style={styles.label}>Event End Datetime</label>
                    <input
                      type="datetime-local"
                      style={styles.input}
                      value={toDatetimeLocalValue(selectedEvent?.end_datetime)}
                      readOnly
                      placeholder="Selected event end datetime"
                    />
                  </div>
                  <div style={{ flex: "0 0 195px", minWidth: 195 }}>
                    <label style={styles.label}>Form Start Datetime *</label>
                    <input
                      type="datetime-local"
                      style={styles.input}
                      value={formStartDate}
                      min={toDatetimeLocalValue(selectedEvent?.start_datetime) || undefined}
                      max={toDatetimeLocalValue(selectedEvent?.end_datetime) || undefined}
                      onChange={(e) => setFormStartDate(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: "0 0 195px", minWidth: 195 }}>
                    <label style={styles.label}>Form End Datetime *</label>
                    <input
                      type="datetime-local"
                      style={styles.input}
                      value={formEndDate}
                      min={toDatetimeLocalValue(selectedEvent?.start_datetime) || undefined}
                      max={toDatetimeLocalValue(selectedEvent?.end_datetime) || undefined}
                      onChange={(e) => setFormEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={styles.label}>Description</label>
                  <textarea style={{ ...styles.input, height: 64, resize: "vertical" }}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional instructions for respondents" />
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Questions */}
          <div style={{ margin: "10px" }}>
            <h3 onClick={() => setSecQuestions((v) => !v)}
              style={{ cursor: "pointer", color: "#4969ab", paddingLeft: 20,
                display: "flex", alignItems: "center" }}>
              Questions
              <span style={{ fontSize: 24, fontWeight: "bold", marginLeft: 10 }}>
                {secQuestions ? "−" : "+"}
              </span>
            </h3>

            {secQuestions && (
              <div style={{ marginLeft: 20, marginRight: 20 }}>
                {questions.map((q, qi) => (
                  <div
                    key={q._key}
                    onDragOver={(e) => {
                      if (draggedQuestionIndex !== null) e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleQuestionDrop(qi);
                    }}
                    style={{
                      margin: "14px 0",
                      background: "#fff",
                      borderRadius: 10,
                      border: "1px solid #e4e6ee",
                      overflow: "hidden",
                      boxShadow: draggedQuestionIndex === qi
                        ? "0 0 0 2px rgba(73, 105, 171, 0.14)"
                        : "none",
                    }}
                  >

                    {/* ── Card top bar ── */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 14px", background: "#f7f8fc",
                      borderBottom: "1px solid #e4e6ee",
                    }}>
                      <Icon
                        name="bars"
                        title="Drag to reorder"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", String(qi));
                          handleQuestionDragStart(qi);
                        }}
                        onDragEnd={handleQuestionDragEnd}
                        style={{
                          cursor: "grab",
                          color: "#8892a6",
                          fontSize: 15,
                          margin: 0,
                          flexShrink: 0,
                        }}
                      />
                      <button
                        type="button"
                        title="Move question up"
                        onClick={() => moveQuestionUp(qi)}
                        disabled={qi === 0}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: qi === 0 ? "not-allowed" : "pointer",
                          padding: 2,
                          opacity: qi === 0 ? 0.35 : 1,
                        }}
                      >
                        <Icon name="arrow up" style={{ color: "#5f6b7a", fontSize: 13, margin: 0 }} />
                      </button>
                      <button
                        type="button"
                        title="Move question down"
                        onClick={() => moveQuestionDown(qi)}
                        disabled={qi === questions.length - 1}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: qi === questions.length - 1 ? "not-allowed" : "pointer",
                          padding: 2,
                          opacity: qi === questions.length - 1 ? 0.35 : 1,
                        }}
                      >
                        <Icon name="arrow down" style={{ color: "#5f6b7a", fontSize: 13, margin: 0 }} />
                      </button>
                      <span style={styles.qBadge}>
                        {q.question_type === "section" ? "§" : `Q${qi + 1}`}
                      </span>
                      {/* Current type pill */}
                      <span style={{
                        fontSize: 11, padding: "3px 10px", borderRadius: 20,
                        fontWeight: 700, background: typeColor(q.question_type), color: "#333",
                        border: "1px solid rgba(0,0,0,0.06)",
                      }}>
                        {TYPE_ICONS[q.question_type] || "○"}&nbsp;
                        {QUESTION_TYPES.find(t => t.code === q.question_type)?.label}
                      </span>
                      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                        {/* Settings toggle */}
                        {TYPE_CONFIG_FIELDS[q.question_type]?.length > 0 && (
                          <button type="button"
                            onClick={() => setOpenConfigIdx(openConfigIdx === qi ? null : qi)}
                            style={{
                              display: "flex", alignItems: "center", gap: 5,
                              fontSize: 12, fontWeight: 600, cursor: "pointer",
                              padding: "4px 10px", borderRadius: 6,
                              border: `1px solid ${openConfigIdx === qi ? "#4969ab" : "#dde0e8"}`,
                              background: openConfigIdx === qi ? "#eef3fc" : "#fff",
                              color: openConfigIdx === qi ? "#0a2352" : "#888",
                              transition: "all 0.15s",
                            }}>
                            <Icon name="setting" style={{ fontSize: 12, margin: 0 }} />
                            Settings
                          </button>
                        )}
                        <button type="button" onClick={() => removeQuestion(qi)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                          <Icon name="trash" style={{ color: "#c0392b", fontSize: 15 }} />
                        </button>
                      </div>
                    </div>

                    {/* ── Card body ── */}
                    <div style={{ padding: "14px 16px" }}>

                      {/* Row 1: Label + Required */}
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
                        <div style={{ flex: 1 }}>
                          <label style={styles.configLabel}>
                            {q.question_type === "section" ? "Section Heading *" : "Question Label *"}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={q.label}
                            onChange={(e) => updateQuestionLocal(qi, { label: e.target.value })}
                            placeholder={q.question_type === "section"
                              ? "e.g. Personal Information"
                              : "e.g. How would you rate us?"}
                            style={{ fontWeight: q.question_type === "section" ? 600 : 400 }}
                          />
                        </div>
                        {q.question_type !== "section" && (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 2 }}>
                            <label style={{ ...styles.configLabel, marginBottom: 6 }}>Required</label>
                            <input type="checkbox"
                              checked={!!q.is_required}
                              onChange={(e) => updateQuestionLocal(qi, { is_required: e.target.checked ? 1 : 0 })}
                              style={{ width: 17, height: 17, cursor: "pointer", accentColor: "#4969ab" }} />
                          </div>
                        )}
                      </div>

                      {/* ── Row 2: Two-step type picker ── */}
                      {(() => {
                        // Find which category the current type belongs to (for default expansion)
                        const currentCatId = TYPE_GROUPS.find(g => g.types.includes(q.question_type))?.id || null;
                        // Use explicitly opened cat, else fall back to the current type's cat
                        const activeCatId  = openCatIdx[qi] !== undefined ? openCatIdx[qi] : currentCatId;
                        const activeGroup  = TYPE_GROUPS.find(g => g.id === activeCatId);

                        return (
                          <div style={{ marginBottom: 4 }}>
                            <label style={{ ...styles.configLabel, marginBottom: 8 }}>Question Type</label>

                            {/* Category pill row */}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                              {TYPE_GROUPS.map((grp) => {
                                const isActive = activeCatId === grp.id;
                                return (
                                  <button
                                    key={grp.id}
                                    type="button"
                                    onClick={() =>
                                      setOpenCatIdx(prev => ({
                                        ...prev,
                                        // toggle off if already open, else switch to this cat
                                        [qi]: prev[qi] === grp.id ? currentCatId : grp.id,
                                      }))
                                    }
                                    style={{
                                      display: "flex", alignItems: "center", gap: 5,
                                      padding: "5px 14px", borderRadius: 20, cursor: "pointer",
                                      fontSize: 12, fontWeight: isActive ? 700 : 400,
                                      border: `1.5px solid ${isActive ? "#4969ab" : "#dde0e8"}`,
                                      background: isActive ? "#eef3fc" : "#fafafa",
                                      color: isActive ? "#0a2352" : "#666",
                                      transition: "all 0.15s", whiteSpace: "nowrap",
                                    }}
                                  >
                                    {grp.label}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Type chips — only the selected category's types */}
                            {activeGroup ? (
                              <div style={{
                                display: "flex", flexWrap: "wrap", gap: 6,
                                paddingLeft: 4, paddingBottom: 4,
                              }}>
                                {activeGroup.types.map((code) => {
                                  const t = QUESTION_TYPES.find(x => x.code === code);
                                  if (!t) return null;
                                  const selected = q.question_type === code;
                                  return (
                                    <button
                                      key={code}
                                      type="button"
                                      onClick={() => {
                                        updateQuestionLocal(qi, { question_type: code, options: [], config: {} });
                                        if (TYPE_CONFIG_FIELDS[code]?.length > 0) {
                                          setOpenConfigIdx(qi);
                                          setConfigTabIdx(prev => ({ ...prev, [qi]: 0 }));
                                        } else {
                                          setOpenConfigIdx(null);
                                        }
                                      }}
                                      style={{
                                        display: "flex", alignItems: "center", gap: 5,
                                        padding: "5px 11px", borderRadius: 20, cursor: "pointer",
                                        fontSize: 12, fontWeight: selected ? 700 : 400,
                                        border: `1.5px solid ${selected ? "#4969ab" : "#dde0e8"}`,
                                        background: selected ? "#eef3fc" : "#fafafa",
                                        color: selected ? "#0a2352" : "#555",
                                        transition: "all 0.15s", whiteSpace: "nowrap",
                                      }}
                                    >
                                      <span style={{ fontSize: 13 }}>{TYPE_ICONS[code] || "○"}</span>
                                      {t.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <p style={{ fontSize: 12, color: "#aaa", paddingLeft: 4, margin: 0 }}>
                                ← select a category above to see types
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Settings panel */}
                      {openConfigIdx === qi && renderConfigPanel(q, qi)}

                      {/* Options for select / radio / checkbox / ranking */}
                      {HAS_OPTIONS.includes(q.question_type) && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
                          <label style={{ ...styles.configLabel, marginBottom: 8 }}>Answer Options</label>
                          {q.options.map((opt, oi) => (
                            <div key={oi} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                              <input type="text" className="form-control" style={{ flex: 1 }}
                                value={opt.option_label}
                                onChange={(e) => handleOptionLabel(qi, oi, e.target.value)}
                                placeholder={`Option ${oi + 1}`} />
                              <span style={{ fontSize: 11, color: "#bbb", minWidth: 70, fontFamily: "monospace" }}>
                                {opt.option_value || "—"}
                              </span>
                              <button type="button" onClick={() => removeOption(qi, oi)}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                                <Icon name="trash" style={{ color: "#c0392b", fontSize: 13 }} />
                              </button>
                            </div>
                          ))}
                          <button style={styles.addOptBtn} onClick={() => addOption(qi)}>
                            + Add option
                          </button>
                        </div>
                      )}

                      {/* Matrix rows + cols */}
                      {HAS_MATRIX.includes(q.question_type) && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f0f0f0",
                          display: "flex", gap: 24 }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ ...styles.configLabel, marginBottom: 6 }}>Rows (statements)</label>
                            {(q.matrix_rows || []).map((row, ri) => (
                              <input key={ri} type="text" className="form-control"
                                style={{ marginBottom: 4 }} value={row}
                                onChange={(e) => updateMatrixRow(qi, ri, e.target.value)}
                                placeholder={`Row ${ri + 1}`} />
                            ))}
                            <button style={styles.addOptBtn} onClick={() => addMatrixRow(qi)}>+ Add row</button>
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ ...styles.configLabel, marginBottom: 6 }}>Columns (scale)</label>
                            {(q.matrix_cols || []).map((col, ci) => (
                              <input key={ci} type="text" className="form-control"
                                style={{ marginBottom: 4 }} value={col}
                                onChange={(e) => updateMatrixCol(qi, ci, e.target.value)}
                                placeholder={`Col ${ci + 1}`} />
                            ))}
                            <button style={styles.addOptBtn} onClick={() => addMatrixCol(qi)}>+ Add column</button>
                          </div>
                        </div>
                      )}

                    </div>{/* end card body */}
                  </div>
                ))}

                <div style={{ padding: "8px 16px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="plus circle"
                    style={{ cursor: "pointer", color: "#007bff", fontSize: 22 }}
                    onClick={addQuestion} />
                  <span style={{ fontSize: 13, color: "#007bff", cursor: "pointer" }}
                    onClick={addQuestion}>
                    Add Question
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Submit row */}
          <div className="submit-row" style={{ display: "flex", gap: 12, padding: "16px 0 16px 10px" }}>
            <button
              onClick={() => { resetForm(); setView("list"); fetchQnrList(); }}
              style={styles.cancelBtn}>
              Cancel
            </button>
            <button className="submit-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editMode ? "Save Changes" : "Save & Generate QR"}
            </button>
            {editMode && (
              <button
                style={{ ...styles.cancelBtn, borderColor: "#0a5e38", color: "#0a5e38" }}
                onClick={() => openQrView(title, slug, null)}>
                View QR
              </button>
            )}
          </div>
        </div>
      )}

      {/* QR VIEW */}
      {view === "qr" && (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <h3 style={{ color: "#0a2352", marginBottom: 4 }}>{qrTitle}</h3>
          <p style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>
            Survey URL:&nbsp;
            <code style={{ background: "#f0ede4", padding: "2px 6px", borderRadius: 4 }}>
              /survey/{qrSlug}
            </code>
          </p>
          <div style={{ display: "inline-block", padding: 16,
            border: "1px solid #e2e0d8", borderRadius: 12,
            background: "#fff", marginBottom: 20 }}>
            <img src={qrDataUrl} alt={`QR for ${qrTitle}`} width={240} height={240} />
          </div>
          <p style={{ fontSize: 13, color: "#666", maxWidth: 340,
            margin: "0 auto 24px", lineHeight: 1.6 }}>
            Circulate this QR. When scanned, the survey form loads automatically.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button style={styles.cancelBtn} onClick={() => { setView("list"); fetchQnrList(); }}>
              Back to List
            </button>
            <button className="submit-btn" onClick={handleDownloadQR}>
              Download QR as PNG
            </button>
            <button style={{ ...styles.cancelBtn, borderColor: "#0a5e38", color: "#0a5e38" }}
              onClick={() => setView("builder")}>
              Edit Questionnaire
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  label:       { display: "block", fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 4 },
  hint:        { fontSize: 11, color: "#999", display: "block" },
  input: {
    width: "100%", boxSizing: "border-box", padding: "7px 10px",
    borderRadius: 6, border: "1px solid #d0cdc4", fontSize: 13,
    background: "#fff", outline: "none", marginBottom: 4,
  },
  configPanel: {
    width: "100%", background: "#f7f8fc", border: "1px solid #dde2f0",
    borderRadius: 10, padding: "16px 18px", marginTop: 4,
  },
  configLabel: { display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 },
  configInput: {
    width: "100%", boxSizing: "border-box", padding: "5px 8px",
    borderRadius: 5, border: "1px solid #cdd1de", fontSize: 12,
    background: "#fff", outline: "none",
  },
  qBadge: {
    fontSize: 11, fontWeight: 700, color: "#555",
    background: "#ebe8df", padding: "2px 10px", borderRadius: 10,
  },
  addOptBtn: {
    fontSize: 12, color: "#4969ab", border: "none",
    background: "none", cursor: "pointer", padding: "4px 0",
  },
  cancelBtn: {
    padding: "9px 22px", borderRadius: 7,
    border: "1px solid #c8c6be", background: "#fff",
    cursor: "pointer", fontSize: 14,
  },
  searchInput: {
    width: "25%", backgroundColor: "white", marginRight: 8,
    padding: 8, borderRadius: 4, border: "1px solid #ced4da", fontSize: 13,
  },
  iconBtn: (bg) => ({
    cursor: "pointer", display: "flex", alignItems: "center",
    justifyContent: "center", borderRadius: "50%", padding: 8,
    backgroundColor: bg, transition: "background-color 0.3s",
  }),
};
